import type { DocumentVersion } from "@prismicio/editor-fields"
import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { preload } from "@prismicio/editor-support/Suspense"
import { queryOptions, usePrefetchQuery } from "@tanstack/react-query"
import { z } from "zod"

import { queryClient } from "../components/providers/QueryProvider"
import { env } from "../env"
import { baseUrl } from "./core"

const release = z.object({
  id: z.string(),
  label: z.string().optional(),
  timestamp: z
    .number()
    .optional()
    .transform((num) => (num ? new Date(num) : undefined)),
  migration: z.boolean().optional(),
})

export type Release = z.TypeOf<typeof release>
export type MigrationRelease = Release & { migration: true }

export type Releases = z.TypeOf<typeof getAllReleasesResponse>

const getAllReleasesResponse = z
  .object({
    results: z.array(release),
  })
  .transform((res) => res.results)

export function createRelease(params: { label: string; date?: Date }) {
  const { label, date } = params

  const url = new URL("releases", baseUrl)
  const body = { label, date: date?.getTime() }

  return request(url, {
    method: "POST",
    body,
    okType: release,
  })
}

export const getAllReleasesQueryOptions = queryOptions({
  queryKey: ["releases"],
  queryFn: getAllReleases,
})

export async function invalidateReleasesListData() {
  await queryClient.invalidateQueries({
    queryKey: getAllReleasesQueryOptions.queryKey,
  })
}

export function updateReleasesListData(
  updateFunction: (data: Releases) => Releases,
) {
  queryClient.setQueryData(
    getAllReleasesQueryOptions.queryKey,
    (oldData: Releases | undefined) => {
      return updateFunction(oldData ?? [])
    },
  )
}

function getAllReleases() {
  const url = new URL("releases", baseUrl)

  url.searchParams.set("include_migration_releases", true.toString())

  return request(url, { okType: getAllReleasesResponse })
}

export function publishDocumentInRelease(metaId: string, releaseId: string) {
  const url = new URL(`documents/${metaId}/draft`, baseUrl)
  const body = { status: "published", release_id: releaseId }

  return request(url, {
    method: "PATCH",
    body,
    okType: z.unknown(),
  })
}

export function publishReleaseNow(
  metaId: string,
  releaseId: string | undefined,
) {
  const url = new URL(`documents/${metaId}/release`, baseUrl)
  const body = { status: "published", release_id: releaseId }

  return request(url, {
    method: "PATCH",
    body,
    // returns a 204, nothing to validate
    okType: z.unknown(),
  })
}

interface CopyDocumentToReleaseArgs {
  documentId: string
  currentReleaseId: string
  targetReleaseId: string
}

export function copyDocumentToRelease(args: CopyDocumentToReleaseArgs) {
  const { documentId, currentReleaseId, targetReleaseId } = args

  const url = new URL(`documents/${documentId}/release`, baseUrl)
  const body = {
    status: `release:${targetReleaseId}`,
    release_id: currentReleaseId,
  }

  return request(url, {
    method: "PATCH",
    body,
    // returns a 204, nothing to validate
    okType: z.unknown(),
  })
}

export function getReleaseId(version: DocumentVersion) {
  if (version.status !== "release") return

  return version.release_id
}

export function updateReleaseSchedule(releaseId: string, date: number) {
  const url = new URL(`releases/${releaseId}/schedule`, baseUrl)

  return request(url, {
    method: "POST",
    body: { date },
    credentials: getRequestCredentials(env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY),
    // returns a 204, nothing to validate
    okType: z.unknown(),
  })
}

export function getReleasePermissions(releaseId: string) {
  return request(new URL(`releases/${releaseId}/permissions`, baseUrl), {
    credentials: getRequestCredentials(env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY),
    okType: ReleasePermissionsSchema,
  })
}

const ReleasePermissionsSchema = z.object({
  canPublish: z.boolean(),
  canWrite: z.boolean(),
})

export function getReleaseSizeQueryOptions(releaseId: string) {
  return queryOptions({
    queryKey: ["releaseSize", releaseId],
    queryFn: () => getReleaseSize(releaseId),
  })
}

export async function invalidateReleaseSizeData(releaseId: string) {
  await queryClient.invalidateQueries({
    queryKey: getReleaseSizeQueryOptions(releaseId).queryKey,
  })
}

function getReleaseSize(releaseId: string) {
  return request(new URL(`releases/${releaseId}/size`, baseUrl), {
    credentials: getRequestCredentials(env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY),
    okType: ReleaseSizeSchema,
  })
}

export function preloadReleasePermissions(releaseId: string) {
  preload(getReleasePermissions, [releaseId])
}

export function usePrefetchReleaseSize(releaseId: string) {
  usePrefetchQuery(getReleaseSizeQueryOptions(releaseId))
}

export function prefetchReleaseSize(releaseId: string) {
  void queryClient.prefetchQuery(getReleaseSizeQueryOptions(releaseId))
}

const ReleaseSizeSchema = z.object({
  documentsCount: z.number(),
  maxDocumentsLimit: z.number(),
})
